import { getBrowserExtensionId } from '@/config/env-config';

export interface ExtensionSigningResult {
  sigma2Bytes: Uint8Array;
  modulus: string;
  publicExponent: number;
}

interface ExtensionResponse {
  action: string;
  status: 'success' | 'error';
  sigma2?: string;
  modulus?: string;
  publicExponent?: number;
  error?: string;
}

const EXTENSION_TIMEOUT_MS = 10_000;

// The key share never leaves the extension; it returns sigma2 for the hex digest.
export function requestExtensionSigning(
  digestHex: string
): Promise<ExtensionSigningResult> {
  const extensionId = getBrowserExtensionId();

  // Safari has no `chrome` global; bare reference (even with `?.`) throws ReferenceError.
  if (
    !extensionId ||
    typeof chrome === 'undefined' ||
    !chrome.runtime?.sendMessage
  ) {
    return Promise.reject(
      new Error(
        'Please install the NewCore browser extension to sign in to this application.'
      )
    );
  }

  const signingPromise = new Promise<ExtensionSigningResult>(
    (resolve, reject) => {
      chrome.runtime.sendMessage(
        extensionId,
        { action: 'signAssertion', signedInfoHash: digestHex },
        (response: ExtensionResponse) => {
          if (chrome.runtime.lastError) {
            reject(
              new Error(
                'Unable to communicate with the NewCore browser extension. ' +
                  'Please check that it is installed and enabled.'
              )
            );

            return;
          }

          if (!response || response.status === 'error') {
            if (response?.error) {
              console.error('Extension signing error:', response.error);
            }

            reject(
              new Error(
                'The NewCore browser extension encountered an error during signing.'
              )
            );

            return;
          }

          if (
            !response.sigma2 ||
            !response.modulus ||
            !response.publicExponent
          ) {
            reject(
              new Error(
                'The NewCore browser extension returned an incomplete response. ' +
                  'Please ensure it is up to date.'
              )
            );

            return;
          }

          const binaryString = atob(response.sigma2);
          const sigma2Bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            sigma2Bytes[i] = binaryString.charCodeAt(i);
          }

          resolve({
            sigma2Bytes,
            modulus: response.modulus,
            publicExponent: response.publicExponent,
          });
        }
      );
    }
  );

  const timeout = new Promise<never>((_, reject) =>
    setTimeout(
      () =>
        reject(
          new Error(
            'The NewCore browser extension did not respond in time. ' +
              'Please check that it is installed and enabled, then try again.'
          )
        ),
      EXTENSION_TIMEOUT_MS
    )
  );

  return Promise.race([signingPromise, timeout]);
}
